/**
 * One date-range value for every analytics page.
 *
 * The pages behind it disagree about how a range is expressed — Marketing
 * filters `marketing_metrics` rows client-side on `period`, the Marketing
 * sections call reports that take `range`/`from`/`to`, and the Funnel reads
 * ISO-week cohorts — so the value stays abstract here and each consumer asks
 * for the shape it needs. Presets keep sending exactly what they always sent,
 * so a page that has not opted into custom ranges is unaffected.
 */

export type DateRangePreset =
  | "7d"
  | "30d"
  | "90d"
  | "180d"
  | "this_week"
  | "all";

export type DateRange =
  | { kind: "preset"; preset: DateRangePreset }
  | { kind: "custom"; from: string; to: string };

/** Days behind each preset. `this_week`/`all` are not day windows. */
export const PRESET_DAYS: Record<string, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "180d": 180,
};

export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function preset(value: DateRangePreset): DateRange {
  return { kind: "preset", preset: value };
}

export function isCustom(
  range: DateRange,
): range is { kind: "custom"; from: string; to: string } {
  return range.kind === "custom";
}

export function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

/** A custom range is only usable once both ends parse and read forwards. */
export function isUsable(range: DateRange): boolean {
  if (range.kind === "preset") return true;
  return (
    isValidIsoDate(range.from) &&
    isValidIsoDate(range.to) &&
    range.from <= range.to
  );
}

export function isoDaysAgo(days: number, today: Date = new Date()): string {
  const date = new Date(today);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

export function todayIso(today: Date = new Date()): string {
  return today.toISOString().slice(0, 10);
}

/**
 * Inclusive `YYYY-MM-DD` bounds for filtering rows that carry a `period` day.
 * `from` is null for `all` (no lower bound); `to` is null for every preset,
 * which keeps preset requests byte-identical to what they sent before.
 */
export function toDayWindow(
  range: DateRange,
  today: Date = new Date(),
): { from: string | null; to: string | null } {
  if (range.kind === "custom") return { from: range.from, to: range.to };
  if (range.preset === "all") return { from: null, to: null };
  if (range.preset === "this_week") {
    return { from: isoDaysAgo(startOfIsoWeekOffset(today), today), to: null };
  }
  return { from: isoDaysAgo(PRESET_DAYS[range.preset] ?? 30, today), to: null };
}

/** Days from `today` back to Monday of the current ISO week. */
function startOfIsoWeekOffset(today: Date): number {
  // getUTCDay: Sunday 0 … Saturday 6. ISO weeks start on Monday.
  const weekday = today.getUTCDay();
  return weekday === 0 ? 6 : weekday - 1;
}

/**
 * Query parameters for the report endpoints (`/marketing/occasion-cohorts`,
 * `/marketing/recurring-users`), which accept either a preset `range` or an
 * explicit `from`/`to` pair.
 */
export function toReportParams(range: DateRange): Record<string, string> {
  if (range.kind === "custom") return { from: range.from, to: range.to };
  return { range: range.preset };
}

/** The ISO week (`YYYY-Www`) a date falls in — the Funnel's cohort key. */
export function isoWeekOf(date: string | Date): string {
  const value = typeof date === "string" ? new Date(`${date}T00:00:00Z`) : date;
  const target = new Date(
    Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()),
  );
  // ISO 8601: the week containing the Thursday of the current week, numbered
  // from the week containing 4 January.
  const dayNumber = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNumber + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const firstDayNumber = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNumber + 3);
  const week =
    1 +
    Math.round(
      (target.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000),
    );
  return `${target.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/**
 * Funnel cohort bounds. The funnel aggregates by ISO week, so a custom range is
 * widened to the weeks its endpoints fall in — callers must say so in the UI.
 */
export function toCohortWeeks(
  range: DateRange,
): { cohort_from: string; cohort_to: string } | null {
  if (range.kind !== "custom") return null;
  return {
    cohort_from: isoWeekOf(range.from),
    cohort_to: isoWeekOf(range.to),
  };
}

/** True when a custom range does not line up with ISO week boundaries. */
export function spansPartialWeeks(range: DateRange): boolean {
  if (range.kind !== "custom") return false;
  const from = new Date(`${range.from}T00:00:00Z`);
  const to = new Date(`${range.to}T00:00:00Z`);
  // Monday start and Sunday end are the only exact fits.
  return from.getUTCDay() !== 1 || to.getUTCDay() !== 0;
}

/**
 * A preset token for a service that only speaks presets (the Snap and Launchpad
 * pre-WhatsApp funnels have their own APIs). A custom window has no preset
 * equivalent, so it widens to `all` — always a superset, never a silent
 * narrowing. Callers must tell the reader that panel is showing more.
 */
export function toPresetToken(range: DateRange): DateRangePreset {
  return range.kind === "custom" ? "all" : range.preset;
}

export function formatRangeLabel(range: DateRange): string {
  if (range.kind === "custom") return `${range.from} → ${range.to}`;
  const labels: Record<DateRangePreset, string> = {
    "7d": "Last 7 days",
    "30d": "Last 30 days",
    "90d": "Last 90 days",
    "180d": "Last 180 days",
    this_week: "This week",
    all: "All time",
  };
  return labels[range.preset];
}

/** Round-trips a range through the URL so a filtered view can be shared. */
export function serializeRange(range: DateRange): string {
  return range.kind === "custom" ? `${range.from}:${range.to}` : range.preset;
}

export function parseRange(
  value: string | null | undefined,
  fallback: DateRange,
): DateRange {
  if (!value) return fallback;
  if (value.includes(":")) {
    const [from, to] = value.split(":", 2);
    const custom: DateRange = { kind: "custom", from, to };
    return isUsable(custom) ? custom : fallback;
  }
  return value in PRESET_DAYS || value === "all" || value === "this_week"
    ? preset(value as DateRangePreset)
    : fallback;
}

/** Why a half-filled or backwards custom draft cannot be applied yet. */
export function invalidRangeReason(draft: {
  from: string;
  to: string;
}): string {
  if (!draft.from || !draft.to) return "Pick both a start and an end date.";
  if (!isValidIsoDate(draft.from) || !isValidIsoDate(draft.to))
    return "Enter valid calendar dates.";
  return "The start date must not be after the end date.";
}
